/**
 * RealtimeContext — SSE Connection Manager
 * ==========================================
 * 
 * Manages a single Server-Sent Events connection per authenticated user.
 * Components subscribe to specific event types via the useRealtime hook.
 * 
 * SECURITY (M4): The main JWT is never passed in the EventSource URL.
 * Instead, we first call POST /api/realtime/sse-token (with the JWT in the
 * Authorization header) to get a short-lived, single-use SSE token. That
 * token is then passed as ?token=<sse_token> to the EventSource URL.
 * This prevents the main JWT from appearing in server logs, browser history,
 * and proxy logs.
 * 
 * NOTE ON fetch vs the shared api client: EventSource cannot go through axios,
 * and the sse-token call is kept alongside it for symmetry. Both build their
 * URL from API_ORIGIN because the paths below already include /api.
 * 
 * Features:
 * - Auto-connect when user authenticates
 * - Auto-reconnect with exponential backoff (1s → 2s → 4s → max 30s)
 * - Connection status tracking (connecting, connected, disconnected, error)
 * - Pub/sub pattern — components subscribe to event types they care about
 * - Online presence tracking
 * - Graceful cleanup on unmount
 * 
 * Usage:
 *   // In a component:
 *   const { subscribe, connectionStatus, onlineUsers } = useRealtime();
 * 
 *   useEffect(() => {
 *     const unsub = subscribe('new_message', (data) => {
 *       // Handle new message
 *       console.log('New message:', data);
 *     });
 *     return unsub;
 *   }, [subscribe]);
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_ORIGIN } from '../config/apiBase';

const RealtimeContext = createContext(null);

// ============================================================================
// PROVIDER
// ============================================================================

export const RealtimeProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // connecting | connected | disconnected | error
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);

  // Refs for managing connection lifecycle
  const eventSourceRef = useRef(null);
  const listenersRef = useRef(new Map()); // Map<eventType, Set<callback>>
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const mountedRef = useRef(true);

  // ------------------------------------------------------------------
  // Subscribe to an event type
  // ------------------------------------------------------------------
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = listenersRef.current.get(eventType);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          listenersRef.current.delete(eventType);
        }
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // Dispatch an event to all subscribers of that type
  // ------------------------------------------------------------------
  const dispatch = useCallback((eventType, data, fullEvent) => {
    const listeners = listenersRef.current.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data, fullEvent);
        } catch (err) {
          console.error(`[Realtime] Error in ${eventType} listener:`, err);
        }
      });
    }

    // Also dispatch to wildcard listeners (subscribe to '*' for all events)
    const wildcardListeners = listenersRef.current.get('*');
    if (wildcardListeners) {
      wildcardListeners.forEach(callback => {
        try {
          callback(data, { ...fullEvent, type: eventType });
        } catch (err) {
          console.error(`[Realtime] Error in wildcard listener:`, err);
        }
      });
    }
  }, []);

  // ------------------------------------------------------------------
  // Connect to SSE stream
  // ------------------------------------------------------------------
  const connect = useCallback(async () => {
    if (!isAuthenticated || !mountedRef.current) return;

    const jwt = localStorage.getItem('red_social_token');
    if (!jwt) return;

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setConnectionStatus('connecting');

    // SECURITY (M4): Get a short-lived SSE token instead of passing
    // the main JWT in the URL. The SSE token is:
    //   - Valid for 60 seconds
    //   - Single-use (consumed on first connection)
    //   - Safe to appear in server logs
    let sseToken = null;
    try {
      // API_ORIGIN, not API_BASE — the path below already includes /api.
      const resp = await fetch(`${API_ORIGIN}/api/realtime/sse-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        sseToken = data.token;
      }
    } catch (err) {
      console.warn('[Realtime] Failed to obtain SSE token:', err);
    }

    // If we couldn't get an SSE token, don't fall back to JWT — schedule
    // a reconnect instead. The backend rejects raw JWTs in the stream URL.
    if (!sseToken) {
      console.warn('[Realtime] No SSE token received, will retry');
      setConnectionStatus('error');
      scheduleReconnect();
      return;
    }

    // API_ORIGIN, not API_BASE — the path below already includes /api.
    const url = `${API_ORIGIN}/api/realtime/stream?token=${encodeURIComponent(sseToken)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    // --- Connection opened ---
    es.onopen = () => {
      if (!mountedRef.current) return;
      setConnectionStatus('connected');
      reconnectAttemptRef.current = 0;
      console.log('[Realtime] SSE connected');
    };

    // --- Connection error ---
    es.onerror = (err) => {
      if (!mountedRef.current) return;
      // Only log the first error, not every retry
      if (reconnectAttemptRef.current === 0) {
        console.warn('[Realtime] SSE connection failed. Backend may not be running.');
      }
      es.close();
      eventSourceRef.current = null;
      setConnectionStatus('error');
      scheduleReconnect();
    };

    // --- Event handlers ---
    // We listen for specific named events (SSE `event:` field)
    
    const eventTypes = [
      'connected',
      'heartbeat',
      'new_message',
      'message_edited',
      'message_deleted',
      'typing',
      'typing_stopped',
      'notification',
      'notification_count',
      'presence',
      'map_update',
      'map_cursor',
      'channel_update',
      'reaction_update',
      'mention',
    ];

    eventTypes.forEach(type => {
      es.addEventListener(type, (e) => {
        if (!mountedRef.current) return;
        try {
          const parsed = JSON.parse(e.data);
          dispatch(type, parsed.data, parsed);
          // Forward map_update to window so CosmicMap (and any other
          // component outside the React tree) can react without prop drilling.
          if (type === 'map_update') {
            window.dispatchEvent(new CustomEvent('map_update', { detail: parsed }));
          }
        } catch (err) {
          console.warn(`[Realtime] Failed to parse ${type}:`, err);
        }
      });
    });

    // Handle presence updates to track online users
    es.addEventListener('presence', (e) => {
      if (!mountedRef.current) return;
      try {
        const parsed = JSON.parse(e.data);
        const { user_id, status } = parsed.data;
        setOnlineUsers(prev => {
          if (status === 'online') {
            return prev.includes(user_id) ? prev : [...prev, user_id];
          } else {
            return prev.filter(id => id !== user_id);
          }
        });
      } catch (err) {
        // Non-critical
      }
    });

  }, [isAuthenticated, dispatch]);

  // ------------------------------------------------------------------
  // Reconnection with exponential backoff (caps at 10 attempts)
  // ------------------------------------------------------------------
  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;

    // Cancel any reconnect already queued. Both connect() (when the SSE token
    // request fails) and es.onerror (on any disconnect) call this, and they
    // share one timeout ref. Without clearing first, the second call
    // overwrites the ref while the original setTimeout still fires — two
    // parallel connects, each requesting a single-use SSE token, and the
    // loser gets a 401 in the console.
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const attempt = reconnectAttemptRef.current;
    
    // Stop retrying after 10 attempts — server is clearly down
    if (attempt >= 10) {
      console.log('[Realtime] Max reconnect attempts reached. SSE disabled until page reload or manual reconnect.');
      setConnectionStatus('disconnected');
      return;
    }
    
    const delay = Math.min(1000 * Math.pow(2, attempt), 30000); // 1s, 2s, 4s, 8s, ... max 30s
    
    // Only log first few attempts to avoid console spam
    if (attempt < 3) {
      console.log(`[Realtime] Reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`);
    }
    setConnectionStatus('disconnected');

    reconnectTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      reconnectAttemptRef.current += 1;
      connect();
    }, delay);
  }, [connect]);

  // ------------------------------------------------------------------
  // Disconnect
  // ------------------------------------------------------------------
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnectionStatus('disconnected');
    setOnlineUsers([]);
  }, []);

  // ------------------------------------------------------------------
  // Connect/disconnect based on auth state
  // ------------------------------------------------------------------
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [isAuthenticated, connect, disconnect]);

  // ------------------------------------------------------------------
  // Cleanup on unmount
  // ------------------------------------------------------------------
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  // ------------------------------------------------------------------
  // Visibility-based reconnect (reconnect when tab becomes visible)
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        if (!eventSourceRef.current || eventSourceRef.current.readyState === EventSource.CLOSED) {
          console.log('[Realtime] Tab visible, reconnecting...');
          reconnectAttemptRef.current = 0;
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isAuthenticated, connect]);

  // ------------------------------------------------------------------
  // Context value
  // ------------------------------------------------------------------
  const value = {
    subscribe,
    connectionStatus,
    onlineUsers,
    onlineCount,
    isConnected: connectionStatus === 'connected',
    disconnect,
    reconnect: () => {
      reconnectAttemptRef.current = 0;
      connect();
    },
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Main hook for accessing the realtime system.
 */
export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

/**
 * Hook to subscribe to a specific event type with automatic cleanup.
 * 
 * @param {string} eventType - The SSE event type to listen for
 * @param {function} callback - Called with (data, fullEvent) when event fires
 * @param {array} deps - Additional dependencies for the callback
 * 
 * Usage:
 *   useRealtimeEvent('new_message', (data) => {
 *     if (data.channel_id === activeChannel) {
 *       setMessages(prev => [...prev, data.message]);
 *     }
 *   }, [activeChannel]);
 */
export const useRealtimeEvent = (eventType, callback, deps = []) => {
  const { subscribe } = useRealtime();
  const callbackRef = useRef(callback);
  
  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...deps]);
  
  useEffect(() => {
    const unsub = subscribe(eventType, (data, fullEvent) => {
      callbackRef.current(data, fullEvent);
    });
    return unsub;
  }, [subscribe, eventType]);
};

/**
 * Hook to check if a specific user is online.
 */
export const useIsUserOnline = (userId) => {
  const { onlineUsers } = useRealtime();
  return onlineUsers.includes(userId);
};

/**
 * Hook for typing indicators in a channel.
 * Returns the list of users currently typing.
 */
export const useTypingIndicator = (channelId) => {
  const [typingUsers, setTypingUsers] = useState([]);
  const timeoutsRef = useRef(new Map());

  useRealtimeEvent('typing', (data) => {
    if (data.channel_id !== channelId) return;

    const key = data.user_id;
    setTypingUsers(prev => {
      const exists = prev.find(u => u.user_id === key);
      if (exists) return prev;
      return [...prev, { user_id: data.user_id, username: data.username }];
    });

    // Auto-remove after 5 seconds of no typing event
    if (timeoutsRef.current.has(key)) {
      clearTimeout(timeoutsRef.current.get(key));
    }
    timeoutsRef.current.set(key, setTimeout(() => {
      setTypingUsers(prev => prev.filter(u => u.user_id !== key));
      timeoutsRef.current.delete(key);
    }, 5000));
  }, [channelId]);

  useRealtimeEvent('typing_stopped', (data) => {
    if (data.channel_id !== channelId) return;
    setTypingUsers(prev => prev.filter(u => u.user_id !== data.user_id));
    if (timeoutsRef.current.has(data.user_id)) {
      clearTimeout(timeoutsRef.current.get(data.user_id));
      timeoutsRef.current.delete(data.user_id);
    }
  }, [channelId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(t => clearTimeout(t));
      timeoutsRef.current.clear();
    };
  }, []);

  return typingUsers;
};

export default RealtimeContext;